import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Pagination, { getPageSize } from '../../Pagination';
import DlBtn from '../../DlBtn';
import {
  payrollApi, PR_IMPORT_FILE_ERRORS, PR_IMPORT_MAX_BYTES, PR_IMPORT_MAX_ROWS, PrAccount, PrImportCommit, PrImportPreview, PrImportRow, PrSchema, PrTier,
} from '../../../utils/api/payroll';
import type { AwCtx } from '../ctx';
import { btn, card, errBox, note, num, pillAmber, pillGreen, pillGrey, pillRed, table, td, th } from '../ui';
import { Dialog, Foot, field, lbl } from '../dialog';
import { TF, historyValue, prErr } from './payrollText';

// ── Payroll › Employees › Import (batch 10 R4 — PAY-7; design §3.1 导入导出) ──────────────────────────────
// A spreadsheet of employees — the template, an export of the list, or the client's own HR file — previewed,
// then committed. The SERVER decides everything (services/employeeImport.ts): which employee a row is (Tax ID
// first, then the employee code, else a new one), what each cell means, and what the form's own validator
// says about the result. The preview IS the commit, run through the employee door and rolled back, so what
// is on screen is what Commit writes; this window only shows it and decides when Commit may be pressed.
// ⛔ Commit is offered only for the preview on screen and only when the server would take it: the same file
// (a different one picked since → stale), a file the server read (`ok`), nothing refused, something to write,
// every name match acknowledged (a new person whose name is already an employee's is never merged — the
// operator ticks that they checked), and a token still alive by this window's own clock. Each of those is a
// commit the server would refuse, and a refused non-GET pages the admins — so none of them is ever sent.
// ⛔ The token's clock: the server keeps a preview for expiresInSec. A ticker re-reads the clock every few
// seconds, and commit() reads it again before sending — a value worked out once at render would leave Commit
// pressable forever. A minute is kept in hand, for the request's own way there.
// ⛔ ANY answer to a commit ends the token: the route takes it before it writes (single use), so a 500 has
// used it as surely as a 410, and a 403 / 404 from the gates would only refuse again — pressing Commit once
// more could fetch nothing but a certain 410, a second refused POST, a second page to the admins. Only a
// request that got no answer at all (no status: the network) leaves it alive. The notice then says why the
// token is gone — the clock or a 410, the employees moved (409), or a fault — never the clock for all three.
// ⛔ A file the route would turn away is never sent: the type by its name (.xlsx / .csv — CSV MIME types
// vary by system) and the size, refused AT the limit as busboy does (api/payroll.ts PR_IMPORT_MAX_BYTES).
// ⛔ Every row can be reached: refused rows first, then name matches, then file order, 100 a page.

const TIERS: PrTier[] = ['C', 'P', '$', 'F'];
const TIER_KEY: Record<PrTier, string> = { C: 'c', P: 'p', $: 'pay', F: 'f' };
const TICK_MS = 5000;
const SPARE_SEC = 60;
const MB = 1024 * 1024;
const ACCEPT = '.xlsx,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const ignoredBox: React.CSSProperties = { ...card, padding: '10px 12px', background: '#FFFBEB', border: '1px solid #F1C232', color: '#92400E' };
const noticeBox: React.CSSProperties = { ...card, padding: '9px 12px', fontSize: 12.5, color: '#92400E', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' };
const ACTION_PILL: Record<string, React.CSSProperties> = { create: pillGreen, update: pillAmber, unchanged: pillGrey, refused: pillRed };
/** Refused rows first, then the rows that only share a name (an employee's, or another new row's of the file), then the file's own order. */
const rank = (r: PrImportRow) => (r.action === 'refused' ? 0 : r.nameMatch ? 1 : 2);
/** Why the window will not send this file: a type the route does not read, or a size it refuses — or ''. */
const fileProblem = (t: TF, f: File): string =>
  !/\.(xlsx|csv)$/i.test(f.name) ? t('prl_imp_err_type') : f.size >= PR_IMPORT_MAX_BYTES ? t('prl_imp_err_size', { mb: PR_IMPORT_MAX_BYTES / MB }) : '';
/** A heading as one line: the template's and the export's are two (English and Thai, one under the other). */
const oneLine = (h: string) => String(h || '').split(/\r?\n/).map(x => x.trim()).filter(Boolean).join(' / ');

export function EmployeeImportWindow({ ctx, schema, onClose, onDone }: { ctx: AwCtx; schema: PrSchema; onClose: () => void; onDone: (r: PrImportCommit) => void }) {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [inputKey, setInputKey] = useState(0);
  const [preview, setPreview] = useState<PrImportPreview | null>(null);
  // What the preview on screen was made from, and when it arrived: Commit is offered only for exactly that file.
  const [previewOf, setPreviewOf] = useState<File | null>(null);
  const [receivedAt, setReceivedAt] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  // The server answered the commit, so this token is gone, and why: the token ran out or was used (410),
  // the employees moved since the preview (409), or anything else — a fault (5xx), a gate (403 / 404).
  const [spent, setSpent] = useState<'' | 'expired' | 'changed' | 'failed'>('');
  const [ack, setAck] = useState(false);
  const [accounts, setAccounts] = useState<PrAccount[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(getPageSize());
  // FS-12: set by the edit path alone — a file chosen, the name-match tick — never by an answer from the server.
  const [dirty, setDirty] = useState(false);
  const ed = <T,>(set: (v: T) => void, v: T) => { setDirty(true); set(v); };

  // The client's expense accounts, so a change of account reads as its code (historyValue), never an id.
  useEffect(() => {
    let alive = true;
    payrollApi.expenseAccounts(ctx.clientId).then(a => { if (alive) setAccounts(a.rows || []); }).catch(() => { if (alive) setAccounts([]); });
    return () => { alive = false; };
  }, [ctx.clientId]);
  const token = preview?.token || '';
  useEffect(() => {
    if (!token) return undefined;
    const h = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(h);
  }, [token]);

  const counts = preview?.counts || null;
  const current = !!preview && !!file && previewOf === file;
  const expiresAt = token && preview ? receivedAt + Math.max(0, Number(preview.expiresInSec) - SPARE_SEC) * 1000 : 0;
  const expired = !!token && (!!spent || now >= expiresAt);
  const needsAck = !!counts && counts.nameMatch > 0;
  const ready = current && !!preview?.ok && !!counts && counts.refused === 0 && counts.create + counts.update > 0 && !!token && (!needsAck || ack) && !expired;
  const ordered = useMemo(() => (preview ? [...preview.rows].sort((a, b) => rank(a) - rank(b) || a.line - b.line) : []), [preview]);
  const shown = ordered.slice((page - 1) * limit, page * limit);
  const show = (f: string, v: any) => historyValue(t, schema, accounts, f, v);
  const fieldName = (f: string) => (f ? t(`prl_f_${f}`, { defaultValue: f }) : '');

  const choose = (f: File | null) => {
    // ⛔ A file the route refuses is never chosen, so it can never be sent.
    const why = f ? fileProblem(t, f) : '';
    if (why) { setErr(why); setFile(null); setInputKey(k => k + 1); return; }
    setErr(''); setFile(f);
    if (f) setDirty(true);
  };
  const runPreview = async () => {
    if (!file || busy) return;
    const why = fileProblem(t, file);
    if (why) { setErr(why); return; }
    setBusy(true); setErr('');
    try {
      const r = await payrollApi.importPreview(ctx.clientId, file);
      const at = Date.now();
      setPreview(r); setPreviewOf(file); setReceivedAt(at); setNow(at); setSpent(''); setAck(false); setPage(1);
    } catch (e: any) { setPreview(null); setPreviewOf(null); setErr(prErr(e, t)); }
    finally { setBusy(false); }
  };
  const commit = async () => {
    if (!ready || busy || !preview) return;
    // ⛔ The clock again, at the moment of sending: the ticker may not have run since the token ran out.
    if (Date.now() >= expiresAt) { setNow(Date.now()); return; }
    setBusy(true); setErr('');
    try {
      const r = await payrollApi.importCommit(ctx.clientId, preview.token, needsAck && ack);
      setDirty(false);
      onDone(r);
    } catch (e: any) {
      const status = Number(e?.status) || 0;
      const code = e?.payload?.code;
      if (status) setSpent(code === 'import_stale' ? 'changed' : code === 'import_expired' ? 'expired' : 'failed');
      // A fault is the server's own: its text is a stack's English, not the operator's to read, in any language.
      setErr(status >= 500 ? t('prl_err_import_failed') : prErr(e, t));
    } finally { setBusy(false); }
  };
  const fileErrorText = (fe: NonNullable<PrImportPreview['fileError']>): string =>
    (PR_IMPORT_FILE_ERRORS as readonly string[]).includes(fe.code)
      ? t(`prl_imp_file_${fe.code}`, { lines: (fe.lines || []).join(', '), values: (fe.values || []).join(', '), max: PR_IMPORT_MAX_ROWS, mb: PR_IMPORT_MAX_BYTES / MB })
      : t('prl_err_generic');
  const stillText = (r: PrImportRow): string => {
    const gaps = TIERS.filter(x => Number(r.still?.[x]) > 0).map(x => `${t(`prl_tier_${TIER_KEY[x]}`)} · ${r.still[x]}`);
    return gaps.length ? t('prl_imp_still', { list: gaps.join(', ') }) : '';
  };
  /** The line a name match points at when the name is another NEW row's of this same file (employeeId ''):
   *  the line the server names, else the first EARLIER new row of the file with that name (and code, when the
   *  match carries one), else with that code — 0 if none. ⛔ Created OR refused: the server pairs the two rows by
   *  the name it read, before the first was refused, so a twin of a refused row is still that row's twin — never
   *  "the same name as an employee", which no employee has. */
  const twinLine = (r: PrImportRow): number => {
    const m = r.nameMatch;
    if (!m || m.employeeId) return 0;
    if (Number(m.line) > 0) return Number(m.line);
    const earlier = (preview?.rows || []).filter(x => x.line < r.line && (x.action === 'create' || x.action === 'refused')).sort((a, b) => a.line - b.line);
    const hit = (m.name ? earlier.find(x => x.name === m.name && (!m.code || x.code === m.code)) || earlier.find(x => x.name === m.name) : undefined)
      || (m.code ? earlier.find(x => x.code === m.code) : undefined);
    return hit ? hit.line : 0;
  };
  const ignored = preview?.ignored || [];
  const exportOnly = preview?.exportOnly || [];

  return (
    <Dialog fullscreen title={t('prl_imp_title')} onClose={onClose} busy={busy} dirty={dirty}
      footer={<Foot onClose={onClose} onSubmit={commit} busy={busy} ready={ready} icon="ti ti-file-import"
        label={current && counts && preview?.ok ? t('prl_imp_commit', { count: counts.create + counts.update }) : t('prl_imp_btn')} t={t} />}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} data-imp="window">
        <div style={note}>{t('prl_imp_help')}</div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label style={{ flex: '1 1 320px' }}>
            <span style={lbl}>{t('prl_imp_file')}</span>
            <input key={inputKey} type="file" accept={ACCEPT} onChange={e => choose(e.target.files?.[0] || null)} style={field} aria-label={t('prl_imp_file')} data-imp="file" />
          </label>
          <button type="button" onClick={runPreview} disabled={!file || busy} style={{ ...btn, opacity: !file || busy ? 0.5 : 1 }} data-imp="preview">
            <i className={busy ? 'ti ti-loader' : 'ti ti-eye'} aria-hidden /> {t('prl_imp_preview')}
          </button>
          <DlBtn style={btn} title={t('prl_tpl_title')} onRun={() => payrollApi.template(ctx.clientId)}>
            <i className="ti ti-download" aria-hidden /> {t('prl_tpl_btn')}
          </DlBtn>
        </div>
        {err && <div style={errBox} data-imp="error">{err}</div>}
        {preview && !current && <div style={noticeBox} data-imp="stale"><i className="ti ti-alert-triangle" aria-hidden /> {t('prl_imp_stale')}</div>}
        {preview && current && expired && (
          <div style={noticeBox} data-imp={spent === 'changed' || spent === 'failed' ? spent : 'expired'}>
            {/* Why the token is gone, once: a fault's own sentence is in the error box above, so it adds none here. */}
            <i className={spent === 'changed' ? 'ti ti-users' : spent === 'failed' ? 'ti ti-alert-triangle' : 'ti ti-clock-exclamation'} aria-hidden />{' '}
            <span style={{ flex: 1 }}>{spent === 'changed' ? t('prl_imp_changed') : spent === 'failed' ? null : t('prl_imp_expired')}</span>
            <button type="button" onClick={runPreview} disabled={busy} style={btn} data-imp="again"><i className="ti ti-refresh" aria-hidden /> {t('prl_imp_preview_again')}</button>
          </div>
        )}
        {preview && (preview.fileError || !preview.ok) && (
          <div style={errBox} data-imp="file-error" data-code={preview.fileError?.code || ''}>{preview.fileError ? fileErrorText(preview.fileError) : t('prl_err_generic')}</div>
        )}
        {preview && preview.ok && counts && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, opacity: current && !expired ? 1 : 0.55 }} data-imp="result">
            {ignored.length > 0 && (
              <div style={ignoredBox} data-imp="ignored">
                <div style={{ fontSize: 13, fontWeight: 800 }}><i className="ti ti-alert-triangle" aria-hidden /> {t('prl_imp_ignored', { count: ignored.length })}</div>
                <ul style={{ margin: '6px 0 0', paddingLeft: 20, fontSize: 12.5, lineHeight: 1.6 }}>
                  {ignored.map(c => (
                    <li key={c.column} data-imp-ignored={c.column}>
                      {t('prl_imp_column_n', { n: c.column })} <b>{oneLine(c.header) || t('prl_imp_no_heading')}</b>
                      {' — '}{c.repeats ? t('prl_imp_twice', { field: fieldName(c.repeats) }) : t('prl_imp_unknown_heading')}
                    </li>
                  ))}
                </ul>
                <div style={{ fontSize: 12, marginTop: 6 }}>{t('prl_imp_ignored_hint')}</div>
              </div>
            )}
            <div style={{ ...note, display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }} data-imp="columns">
              <span style={{ fontWeight: 700 }}>{preview.kind === 'xlsx' ? t('prl_imp_from_xlsx', { sheet: preview.sheet, row: preview.headerRow }) : t('prl_imp_from_csv', { row: preview.headerRow })}</span>
              {preview.columns.map(c => {
                const name = fieldName(c.field);
                const h = oneLine(c.header);
                return <span key={c.column} style={{ ...pillGrey, fontWeight: 600 }} data-imp-read={c.field}>{h}{name && name.toLowerCase() !== h.toLowerCase() ? ` → ${name}` : ''}</span>;
              })}
            </div>
            {exportOnly.length > 0 && (
              <div style={{ ...note, color: 'var(--text-tertiary)' }} data-imp="export-only">{t('prl_imp_export_only', { list: exportOnly.map(c => oneLine(c.header)).join(', ') })}</div>
            )}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }} data-imp="counts">
              <span style={pillGrey} data-imp-count="rows">{t('prl_imp_n_rows', { count: counts.rows })}</span>
              <span style={pillGreen} data-imp-count="create">{t('prl_imp_n_create', { count: counts.create })}</span>
              <span style={pillAmber} data-imp-count="update">{t('prl_imp_n_update', { count: counts.update })}</span>
              <span style={pillGrey} data-imp-count="unchanged">{t('prl_imp_n_unchanged', { count: counts.unchanged })}</span>
              <span style={counts.refused ? pillRed : pillGrey} data-imp-count="refused">{t('prl_imp_n_refused', { count: counts.refused })}</span>
              {counts.nameMatch > 0 && <span style={pillAmber} data-imp-count="name-match">{t('prl_imp_n_name_match', { count: counts.nameMatch })}</span>}
            </div>
            {counts.refused > 0 && <div style={note} data-imp="fix">{t('prl_imp_fix')}</div>}
            {counts.refused === 0 && counts.create + counts.update === 0 && <div style={note} data-imp="nothing">{t('prl_imp_nothing')}</div>}
            {needsAck && (
              <label style={{ ...noticeBox, cursor: 'pointer' }}>
                <input type="checkbox" checked={ack} onChange={e => ed(setAck, e.target.checked)} disabled={!current || expired} data-imp="ack" />
                <span>{t('prl_imp_ack', { count: counts.nameMatch })}</span>
              </label>
            )}
            <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
              <table style={table}>
                <thead>
                  <tr>
                    <th style={{ ...th, textAlign: 'right' }}>{t('prl_imp_col_line')}</th>
                    <th style={th}>{t('prl_imp_col_action')}</th>
                    <th style={th}>{t('prl_col_code')}</th>
                    <th style={th}>{t('prl_col_name')}</th>
                    <th style={th}>{t('prl_imp_col_changes')}</th>
                    <th style={th}>{t('prl_imp_col_problems')}</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map(r => (
                    <tr key={r.line} data-imp-line={r.line} data-imp-action={r.action} data-imp-name-match={r.nameMatch ? '1' : undefined}
                      style={r.action === 'refused' ? { background: '#FFF8F8' } : r.nameMatch ? { background: '#FFFBEB' } : undefined}>
                      <td style={num}>{r.line}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>
                        <span style={ACTION_PILL[r.action] || pillGrey}>{t(`prl_imp_action_${r.action}`)}</span>
                        {r.matchedBy && <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 3 }}>{t(`prl_imp_matched_${r.matchedBy}`)}</div>}
                      </td>
                      <td style={{ ...td, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{r.code || '—'}</td>
                      <td style={td}>
                        {r.name || '—'}
                        {r.nameMatch && (
                          <div style={{ fontSize: 11.5, color: '#92400E', marginTop: 3 }} data-imp-same-name={r.nameMatch.employeeId ? 'employee' : 'file'}>
                            {/* An employee's name, or another new row's of this file (the same person twice): never merged either way. */}
                            <i className="ti ti-users" aria-hidden /> {twinLine(r)
                              ? t('prl_imp_name_match_file', { line: twinLine(r) })
                              : t('prl_imp_name_match', { name: r.nameMatch.name, code: r.nameMatch.code || '—' })}
                          </div>
                        )}
                      </td>
                      <td style={{ ...td, fontSize: 12 }} data-imp-changes>
                        {!r.changes.length ? <span style={{ color: 'var(--text-tertiary)' }}>—</span> : r.changes.map((c, i) => (
                          <div key={`${c.field}:${i}`} data-imp-change={c.field}>
                            <span style={{ color: 'var(--text-secondary)' }}>{fieldName(c.field)}:</span>{' '}
                            {c.old === null || c.old === undefined ? null : <><span style={{ color: 'var(--text-tertiary)' }} data-old>{show(c.field, c.old)}</span> → </>}
                            <span data-new>{show(c.field, c.neu)}</span>
                            {/* The log keeps a bank account as ••••last4: a new number with the same last four would read as no change. */}
                            {c.field === 'bank_account' && c.old !== null && c.old !== undefined && show(c.field, c.old) === show(c.field, c.neu) && (
                              <div style={{ fontSize: 11.5, color: '#92400E' }} data-imp-same-tail>{t('prl_imp_bank_same_tail')}</div>
                            )}
                          </div>
                        ))}
                      </td>
                      <td style={{ ...td, fontSize: 12, maxWidth: 420 }}>
                        {r.problems.map((p, i) => (
                          <div key={`${p.field}:${p.code}:${i}`} style={{ color: '#B42318' }} data-imp-problem={`${p.field}:${p.code}`}>
                            {p.field ? `${fieldName(p.field)} — ` : ''}{t(`prl_imp_problem_${p.code}`)}
                          </div>
                        ))}
                        {r.action !== 'refused' && stillText(r) && <div style={{ color: 'var(--text-tertiary)' }} data-imp-still>{stillText(r)}</div>}
                        {/* The statutory forms keep their own names, in every language (testPayrollI18n rule 3). */}
                        {r.wageSync && <div style={{ color: '#92400E' }} data-imp-wage><i className="ti ti-arrows-exchange" aria-hidden /> {t('prl_imp_wage_sync', { forms: 'ภ.ง.ด.1 · สปส.1-10' })}</div>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={page} total={ordered.length} limit={limit} onPage={setPage} onLimit={n => { setLimit(n); setPage(1); }} />
          </div>
        )}
      </div>
    </Dialog>
  );
}
